import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
// Supabase functions for usage tracking (replaces Firebase Firestore)
import { supabase } from '../supabaseClient';
import Result from './Result';
import EmailFixEngine from './EmailFixEngine';
import ErrorBoundary from './ErrorBoundary';

const SYSTEM_PROMPT = `You are LeadsLense AI, a reply-focused cold email generation engine. Your job is to generate ultra-personalized, high-converting cold emails that get responses.

Output Format Requirement: You MUST output a valid JSON object with these exact keys (no markdown, no code fences):
{
  "subject_line": "Brief, compelling subject line",
  "email_body": "Full personalized email",
  "reply_probability": 85,
  "psychological_flaw": "The main objection or hesitation the prospect might have",
  "instant_fix": "How your email overcomes this psychological barrier"
}`;

const INDUSTRY_OPTIONS = ['General', 'Fintech', 'Healthcare', 'SaaS', 'Freelancer', 'E-commerce', 'Real Estate', 'EdTech'];

const INDUSTRY_GUIDELINES = {
  General: 'Focus on clear value proposition, ROI, and relationship building.',
  Fintech: 'Emphasize security, trust, compliance, regulatory excellence, and data protection. Use formal, data-driven language.',
  Healthcare: 'Focus on HIPAA compliance, patient outcomes, data security, efficiency gains. Maintain empathetic yet corporate tone.',
  SaaS: 'Highlight ROI, growth metrics, scalability, innovation, and modern tech-startup language. Focus on rapid scaling.',
  Freelancer: 'Emphasize speed, flexibility, custom solutions, portfolio alignment. Quick turnaround and adaptability are key.',
  'E-commerce': 'Focus on conversion rates, customer retention, supply chain efficiency, modern consumer brand voice.',
  'Real Estate': 'Highlight market expertise, high-ticket lead generation, property value, professional relationship building.',
  EdTech: 'Focus on student engagement, learning platform innovation, scalability for institutions, educational impact.'
};

const parseJsonResponse = (rawText) => {
  const cleaned = rawText.replace(/```json/gi, '').replace(/```/g, '').trim();
  const start = cleaned.indexOf('{');
  const end = cleaned.lastIndexOf('}') + 1;

  if (start === -1 || end <= start) {
    throw new Error('Unable to locate a JSON payload in the model response.');
  }

  return JSON.parse(cleaned.slice(start, end));
};

export default function Workspace({ onBackToLanding }) {
  const [jobDescription, setJobDescription] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('General');
  const [loading, setLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState('');
  // Authentication and limit handling
  const { user, supabase } = useAuth();
  const [dailyCount, setDailyCount] = useState(0);
  const [isPremium, setIsPremium] = useState(false);

  // Load usage data from Supabase
  useEffect(() => {
    if (!user) return;
    const fetchUsage = async () => {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('daily_count, last_count_date, is_premium')
        .eq('id', user.id)
        .single();
      if (error && error.code === 'PGRST116') {
        // No profile yet, create one
        const today = new Date().toISOString().split('T')[0];
        await supabase.from('user_profiles').insert({
          id: user.id,
          daily_count: 0,
          last_count_date: today,
          is_premium: false,
        });
        setDailyCount(0);
        setIsPremium(false);
        return;
      }
      if (error) {
        console.error('Supabase usage fetch error:', error);
        return;
      }
      const today = new Date().toISOString().split('T')[0];
      const count = data.last_count_date === today ? data.daily_count : 0;
      setDailyCount(count);
      setIsPremium(data.is_premium);
    };
    fetchUsage();
  }, [user, supabase]);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showPaymentSection, setShowPaymentSection] = useState(false);
  const DAILY_LIMIT = 5;

  const handleGenerate = async () => {
    // Enforce daily limit before proceeding (skip for premium users)
    if (!isPremium && dailyCount >= DAILY_LIMIT) {
      setShowLimitModal(true);
      return;
    }
    const description = jobDescription.trim();

    if (!description) {
      setError('Paste a client requirement or job description to generate your email.');
      setAnalysisResult(null);
      return;
    }

    setError('');
    setLoading(true);
    setAnalysisResult(null);

    const apiKey = process.env.REACT_APP_OPENROUTER_API_KEY;

    if (!apiKey) {
      setLoading(false);
      setError('REACT_APP_OPENROUTER_API_KEY environment variable is not set.');
      return;
    }

    try {
      const userMessage = `[LAYER 1: TARGET INDUSTRY RULES]
Industry: ${selectedIndustry}
${INDUSTRY_GUIDELINES[selectedIndustry]}

[LAYER 2: CLIENT INTENT & CONTEXT]
Analyze the following brief to identify the client's core hiring intent, technical needs, and decision-making criteria:

${description}

[LAYER 3: GENERATE REPLY-FOCUSED EMAIL]
Create an ultra-personalized, high-converting cold email for this opportunity. The email must:
1. Address a specific psychological barrier or pain point
2. Provide an immediate, actionable solution
3. Be brief, direct, and formatted for maximum reply rates

Respond ONLY with valid JSON (no markdown):
{
  "subject_line": "Brief compelling subject line",
  "email_body": "Full personalized email",
  "reply_probability": 85,
  "psychological_flaw": "The main objection",
  "instant_fix": "How we overcome it"
}`;

      const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'openrouter/free',
          messages: [
            { role: 'system', content: SYSTEM_PROMPT },
            { role: 'user', content: userMessage }
          ]
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      const rawContent = data?.choices?.[0]?.message?.content || '';
      const parsed = parseJsonResponse(rawContent);

      setAnalysisResult(parsed);
      // Increment count on successful generation and persist to Supabase
      const newCount = dailyCount + 1;
      setDailyCount(newCount);
      const today = new Date().toISOString().split('T')[0];
      await supabase
        .from('user_profiles')
        .update({ daily_count: newCount, last_count_date: today })
        .eq('id', user.id);
    } catch (err) {
      console.error(err);
      setError('Generation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-[#fdfbf7] text-gray-950 font-sans antialiased">
      <div className="mx-auto flex max-w-7xl flex-col px-4 pb-14 pt-5 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-[30px] border border-gray-200/60 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.05)]">
          <div className="flex flex-col lg:grid lg:grid-cols-[250px_1fr]">
            <aside className="border-b border-gray-200/60 bg-[#fffdfa] p-5 lg:border-b-0 lg:border-r">
                <div className="flex items-center gap-2">
                  <img 
                    src="/logo2.jpg" 
                    alt="Logo" 
                    className="h-9 w-9 rounded-full object-cover inline-block align-middle"
                    onError={(e) => {
                      // If local logo2.jpg is missing in public folder, load fallback
                      e.target.src = "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=100&auto=format&fit=crop&q=80";
                    }}
                  />
                  <span className="text-xl font-black text-gray-950 inline-block align-middle">LeadsLense AI</span>
                </div>

              <div className="mt-6 space-y-2">
                <div className="rounded-[18px] bg-[#f8f3e7] px-4 py-3 text-sm font-bold text-gray-900">
                  Workspace
                </div>
                <div className="rounded-[18px] px-4 py-3 text-sm text-gray-700">Templates</div>
                <div className="rounded-[18px] px-4 py-3 text-sm text-gray-700">Performance</div>
              </div>

              <div className="mt-8 rounded-[22px] border border-gray-200 bg-white p-4">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-700">Light Mode</p>
                <p className="mt-3 text-sm leading-7 text-gray-700">
                  Build premium reply-focused emails with soft cream surfaces, white cards, and clean contrast.
                </p>
              </div>
            </aside>

            <div className="p-5 sm:p-6 lg:p-8">
              <div className="flex flex-col gap-4 border-b border-gray-200/60 pb-5 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h1 className="mt-3 text-3xl font-black tracking-[-0.05em] text-gray-950 sm:text-[2.1rem]">
                    Generate a premium cold email in one clean flow
                  </h1>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-full border border-gray-200 bg-[#fffdfa] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.2em] text-gray-900">
                    Free / Pro
                  </span>
                  <button
                    type="button"
                    onClick={onBackToLanding}
                    className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-gray-900"
                  >
                    Back to Landing
                  </button>
                </div>
              </div>

              <div className="mt-6 grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
                <div className="rounded-[24px] border border-gray-200/60 bg-[#fffdfa] p-4 sm:p-5">
                  <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.24em] text-gray-700">
                    Industry
                  </label>
                  <select
                    value={selectedIndustry}
                    onChange={(e) => setSelectedIndustry(e.target.value)}
                    className="w-full rounded-[18px] border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-950 outline-none"
                  >
                    {INDUSTRY_OPTIONS.map((industry) => (
                      <option key={industry} value={industry}>
                        {industry}
                      </option>
                    ))}
                  </select>

                  <label className="mt-5 mb-2 block text-[11px] font-bold uppercase tracking-[0.24em] text-gray-700">
                    Client Requirement
                  </label>
                  <textarea
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                    placeholder="Paste the prospect, company, offer, and desired outcome here…"
                    className="min-h-[280px] w-full rounded-[22px] border border-gray-200 bg-white px-4 py-4 text-sm leading-7 text-gray-950 outline-none resize-none"
                  />

                  {error && (
                    <div className="mt-4 rounded-[18px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      ⚠ {error}
                    </div>
                  )}

                  <div className="mt-5 flex justify-center">
                    <button
                      type="button"
                      onClick={handleGenerate}
                       disabled={loading || dailyCount >= DAILY_LIMIT}
                      className={`rounded-full px-8 py-3 text-sm font-bold uppercase tracking-[0.14em] transition-all duration-200 ${
                         loading || dailyCount >= DAILY_LIMIT
                          ? 'cursor-not-allowed border border-gray-200 bg-[#f8f3e7] text-gray-700'
                          : 'border border-gray-200 bg-[#fffdfa] text-gray-950 shadow-[0_10px_24px_rgba(15,23,42,0.08)]'
                      }`}
                    >
                      {loading ? 'Generating…' : 'Generate Email'}
                    </button>
                  </div>
                </div>

                <div className="rounded-[24px] border border-gray-200/60 bg-white p-4 sm:p-5">
                  <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-gray-700">Quick Guidance</p>
                  <div className="mt-4 space-y-3 text-sm leading-7 text-gray-700">
                    <p>• Lead with a clear, human problem statement.</p>
                    <p>• Use one immediate benefit in the first sentence.</p>
                    <p>• End with a low-friction next step for the prospect.</p>
                  </div>
                </div>
              </div>

                  {analysisResult && (
                    <ErrorBoundary>
                      <Result data={analysisResult} />
                    </ErrorBoundary>
                  )}

                  {/* Email Fix Engine component */}
                  {user && (
                    <ErrorBoundary>
                      <EmailFixEngine user={user} />
                    </ErrorBoundary>
                  )}

                 {/* Daily limit modal */}
                {showLimitModal && (
                  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white rounded-[24px] p-6 max-w-md w-full shadow-lg">
                      <h2 className="text-xl font-bold mb-4 text-gray-900">Free credits exhausted</h2>
                      <p className="mb-4 text-gray-700">
                        Your 5 free daily credits have expired. Upgrade to our Premium Plan for ₹799 for unlimited monthly access!
                      </p>
                       {showPaymentSection && (
                         <div className="mb-4">
                           <p className="text-gray-600 mb-2">
                             <a
                               href="upi://pay?pa=YOUR_UPI_ID@okaxis&pn=YOUR_NAME&am=799&cu=INR"
                               className="text-blue-600 underline"
                             >
                               Pay ₹799 via UPI
                             </a>
                           </p>
                           <p className="text-sm text-gray-500 mt-2">
                             Please take a screenshot of the payment confirmation and email it to us to activate your premium account.
                           </p>
                         </div>
                       )}
                      <div className="flex justify-end space-x-2">
                      <button
                        type="button"
                        onClick={() => setShowPaymentSection(!showPaymentSection)}
                        className="px-4 py-2 bg-[#fffdfa] border border-gray-200 rounded-full text-gray-950"
                      >
                        {showPaymentSection ? 'Hide Payment' : 'Upgrade'}
                      </button>
                        {showPaymentSection && (
                          <button
                            type="button"
                            onClick={async () => {
                              if (!user) return;
                              // Update premium flag in Supabase instead of legacy Firebase
                              const { error } = await supabase
                                .from('user_profiles')
                                .update({ is_premium: true })
                                .eq('id', user.id);
                              if (error) {
                                console.error('Failed to update premium status:', error);
                                setError('Unable to confirm payment. Please try again later.');
                                return;
                              }
                              setIsPremium(true);
                              setShowLimitModal(false);
                            }}
                            className="px-4 py-2 bg-green-600 text-white rounded-full"
                          >
                            Confirm Payment
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setShowLimitModal(false)}
                          className="px-4 py-2 bg-[#f8f3e7] border border-gray-200 rounded-full text-gray-700"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
    </ErrorBoundary>
  );
}
